import { useGame } from '../state/useGame';
import { ZONES } from '../data';
import { getMonster, type MonsterSpec } from '../domain/monsters/spec';
import { totalBonuses } from '../domain/economy/equipment';
import { isZoneUnlocked, monsterAvailability } from '../domain/quests/eligibility';
import { BOSS_MONSTER_ID, bossPhase, PHASE_LABELS } from '../domain/monsters/boss';

const ZONE = ZONES.zones[0]!;

function MonsterCard({ monster, isBoss }: { monster: MonsterSpec; isBoss: boolean }) {
  const state = useGame((s) => s.state);
  const setActiveMonster = useGame((s) => s.setActiveMonster);
  const pushToast = useGame((s) => s.pushToast);

  const availability = monsterAvailability(state, monster.id);
  const isFighting = state.active.kind === 'combat' && state.active.monsterId === monster.id;

  return (
    <div
      className={`monster tier-${monster.tier} ${isFighting ? 'fighting' : ''} ${isBoss ? 'boss' : ''}`}
      data-monster-id={monster.id}
      data-unlocked={availability.available}
    >
      <img
        src={`/assets/icons/${monster.portrait}`}
        alt={`${monster.name} portrait`}
        width={96}
        height={96}
        loading="lazy"
        style={{ borderRadius: 6, imageRendering: 'pixelated', background: '#1a1d24', display: 'block', marginBottom: 8 }}
      />
      <div className="name">
        {monster.name}
        {isBoss && <span className="tag" style={{ marginLeft: 6 }}>Boss</span>}
      </div>
      <div className="meta">
        HP {monster.hp} · ATK {monster.attack} · DEF {monster.defence}
        <br />
        Tier {monster.tier} · {monster.zone.replaceAll('_', ' ')}
      </div>
      <p style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--ui-text-dim)' }}>
        {monster.flavor}
      </p>
      {!availability.available && (
        <p className="lock-reason" style={{ fontSize: 12, color: 'var(--warn, #e0a94a)' }}>
          🔒 {availability.reason}
        </p>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        {isFighting ? (
          <span className="tag" aria-label={`Currently fighting ${monster.name}`}>Fighting</span>
        ) : (
          <button
            className="primary"
            disabled={!availability.available}
            onClick={() => setActiveMonster(monster.id)}
            aria-label={`Fight ${monster.name}`}
          >
            Fight
          </button>
        )}
        <button
          aria-label={`Inspect ${monster.name}`}
          onClick={() => pushToast(`${monster.name}: ${monster.flavor}`, 'good')}
        >
          Inspect
        </button>
      </div>
    </div>
  );
}

export function CombatPage() {
  const state = useGame((s) => s.state);
  const bossQuote = useGame((s) => s.bossQuote);
  const active = state.active;

  const unlocked = isZoneUnlocked(state);
  const playerHp = state.hp.player;
  const maxPlayerHp = 100 + totalBonuses(state.equipment).hp;
  const monsterHp = state.hp.monster;
  const fighting = active.kind === 'combat' ? getMonster(active.monsterId) : null;
  const fightingBoss = fighting?.id === BOSS_MONSTER_ID;
  const phase =
    fightingBoss && monsterHp !== null ? bossPhase(monsterHp, fighting!.hp) : null;

  const roster = ZONE.monsterIds.map((id) => getMonster(id as MonsterSpec['id']));
  const boss = ZONE.boss ? getMonster(ZONE.boss as MonsterSpec['id']) : null;

  return (
    <>
      <h1 className="page-title">
        {unlocked ? `Combat — ${ZONE.name}` : 'Combat — /tmp pantry drill'}
      </h1>
      <p className="page-subtitle">
        {unlocked
          ? 'Pick a monster. Combat runs automatically. Drops land in the bank.'
          : 'The Codex Quarry is sealed until Roaches in /tmp is complete. One roach drill remains available.'}
      </p>

      {active.kind === 'combat' && fighting && (
        <div
          className="card"
          style={{ borderColor: 'var(--pipeline-green)' }}
          data-combat-monster={fighting.id}
        >
          <strong>Auto-attacking {fighting.name}.</strong>{' '}
          <button
            className="danger"
            aria-label={`Stop fighting ${fighting.name}`}
            onClick={useGame.getState().stopActive}
          >
            Stop
          </button>
          <div style={{ marginTop: 10 }}>
            <div className="meta">Player HP: {playerHp} / {maxPlayerHp}</div>
            <div
              className="xp-bar hp-bar"
              role="progressbar"
              aria-label="Player HP"
              aria-valuenow={playerHp}
              aria-valuemin={0}
              aria-valuemax={maxPlayerHp}
            >
              <div className="fill" style={{ width: `${(playerHp / maxPlayerHp) * 100}%` }} />
            </div>
            <div className="meta" style={{ marginTop: 8 }}>
              Monster HP{monsterHp !== null ? `: ${monsterHp} / ${fighting.hp}` : ''}
            </div>
            {monsterHp !== null && (
              <div
                className="xp-bar hp-bar"
                role="progressbar"
                aria-label={`${fighting.name} HP`}
                aria-valuenow={monsterHp}
                aria-valuemin={0}
                aria-valuemax={fighting.hp}
              >
                <div className="fill" style={{ width: `${(monsterHp / fighting.hp) * 100}%` }} />
              </div>
            )}
            <div aria-live="polite" data-combat-log>
              {fightingBoss && phase !== null && (
                <div className="meta" style={{ marginTop: 8 }} data-boss-phase={phase}>
                  {PHASE_LABELS[phase]}
                </div>
              )}
              {fightingBoss && bossQuote && (
                <div className="meta" style={{ marginTop: 4 }} data-boss-quote>
                  Parakeet operator log: “{bossQuote}”
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="zone">
        {roster.map((m) => (
          <MonsterCard key={m.id} monster={m} isBoss={false} />
        ))}
        {boss && <MonsterCard key={boss.id} monster={boss} isBoss={true} />}
      </div>
    </>
  );
}
